import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, push, serverTimestamp, onValue, remove } from 'firebase/database';
import { db } from '../../utils/firebase';
import { useLanguage } from '../../context/LanguageContext';

export default function AdminEventDashboard() {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const t = (frText, enText) => (isFr ? frText : enText);
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const categories = isFr
    ? ['Ligue des champions', 'Cérémonie de remise de prix', 'Avant-première cinéma', 'Personnalisé']
    : ['Champions League', 'Awards Show', 'Movie Premiere', 'Custom'];

  useEffect(() => {
    const eventsRef = ref(db, 'events');
    return onValue(
      eventsRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const list = Object.entries(data).map(([key, value]) => ({ id: key, ...value }));
        setEvents(list);
      },
      (err) => console.error('Failed to load events:', err)
    );
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    if (!title || !category || !dateTime) {
      setError(t('Veuillez remplir tous les champs obligatoires.', 'Please fill in all required fields.'));
      return;
    }
    setSubmitting(true);
    try {
      await push(ref(db, 'events'), {
        title,
        category,
        description,
        dateTime,
        createdAt: serverTimestamp(),
      });
      setModalOpen(false);
      resetForm();
    } catch (err) {
      console.error('Error creating event:', err);
      setError(t("Échec de la création de l'événement.", 'Failed to create event.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setCategory('');
    setDescription('');
    setDateTime('');
    setError(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Supprimer cet événement ?', 'Delete this event?'))) return;
    try {
      await remove(ref(db, `events/${id}`));
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t('Gestion des événements', 'Event management')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('Créez et supprimez les annonces affichées sur la page publique des événements.', 'Create and remove listings shown on the public events page.')}</p>
        </div>
        <button type="button" onClick={() => setModalOpen(true)} className="ui-btn-primary shrink-0">
          {t('Nouvel événement', 'New event')}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/40 shadow-panel">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800 text-sm">
            <thead className="bg-slate-900/80">
              <tr>
                {[t('Titre', 'Title'), t('Catégorie', 'Category'), t('Date et heure', 'Date & time'), t('Actions', 'Actions')].map((header) => (
                  <th key={header} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80">
              {events.map((event) => (
                <tr key={event.id} className="transition hover:bg-slate-900/60">
                  <td className="whitespace-nowrap px-5 py-4 font-medium text-slate-200">{event.title}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-400">{event.category}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-400">
                    {event.dateTime ? new Date(event.dateTime).toLocaleString() : '—'}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4">
                    <button
                      type="button"
                      onClick={() => handleDelete(event.id)}
                      className="rounded-lg border border-rose-900/50 bg-rose-950/30 px-3 py-1.5 text-xs font-medium text-rose-200 transition hover:bg-rose-950/50"
                    >
                      {t('Supprimer', 'Delete')}
                    </button>
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-slate-500">
                    {t('Aucun événement planifié.', 'No events scheduled.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="ui-panel w-full max-w-lg p-6 sm:p-8"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="text-xl font-bold text-white">{t('Nouvel événement', 'New event')}</h2>
              {error ? <div className="mt-3 rounded-lg border border-rose-900/50 bg-rose-950/30 px-3 py-2 text-sm text-rose-200">{error}</div> : null}
              <form onSubmit={handleCreate} className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-400">{t('Titre *', 'Title *')}</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="ui-input"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-400">{t('Catégorie *', 'Category *')}</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="ui-select"
                  >
                    <option value="">{t('Sélectionnez une catégorie', 'Select category')}</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-400">{t('Date et heure *', 'Date & time *')}</label>
                  <input
                    type="datetime-local"
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                    className="ui-input"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-400">{t('Description', 'Description')}</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="ui-input min-h-[100px] resize-y"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      resetForm();
                    }}
                    className="ui-btn-secondary"
                  >
                    {t('Annuler', 'Cancel')}
                  </button>
                  <button type="submit" disabled={submitting} className="ui-btn-primary">
                    {submitting ? t('Enregistrement…', 'Saving...') : t("Enregistrer l'événement", 'Save event')}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
